import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Skeleton, Tab, Tabs } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel } from '../../components/ui';
import { Orders } from '../../store/slices';
import { IOneOrder, IStore } from '../../store/types';
import OrdersTable from '../../components/OrdersTable';
import OrdersFilters from '../../components/OrdersFilters';
import OrdersReport from '../../components/OrdersReport';
import OrdersProduction from '../../components/OrdersProduction';
import PrintFiles from './PrintFiles';
import { useCan } from '../../auth/useCan';
import { ORDERS_RW } from '../../auth/requirements';
import { companyNameFor } from '../../components/LeftNavigationBar/navConfig';
import RefreshIcon from '@mui/icons-material/Refresh';

const DEFAULT_PAGING_STATE = {
    perPage: 100,
    page: 1,
    total: 0,
    sort: 'createdAt',
    sortDirection: 'DESC',
};

const OrdersPage = () => {
    const { orders, status, queryParams } = useSelector<IStore, { orders: Record<string, IOneOrder>[]; status: string; queryParams: any }>((state) => ({
        orders: Object.values(state.orders.data),
        status: state.orders.status,
        queryParams: state.orders.queryParams,
    }));
    const { id } = useParams();
    const dispatch = useDispatch();
    const canManageOrders = useCan(ORDERS_RW);
    const [isReportPopUp, setReportPopUp] = useState(false);
    const [isProductionPopUp, setProductionPopUp] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    // Bumped on "Clear filters" so the uncontrolled search field remounts empty.
    const [filtersKey, setFiltersKey] = useState(0);

    const loadOrders = (params: any) => {
        dispatch<any>(Orders.getOrders(params));
    };

    const clearFilters = () => {
        dispatch(Orders.clearFilters());
        setFiltersKey((key) => key + 1);
        loadOrders({
            perPage: 25,
            page: 1,
            sort: 'createdAt',
            sortDirection: 'DESC',
            orderStatusId: null,
            createdAt_max: null,
            search: null,
            companyId: id,
        });
    };

    useEffect(() => {
        if (tabValue === 0) {
            loadOrders({
                ...queryParams,
                companyId: id,
                page: queryParams?.page || DEFAULT_PAGING_STATE.page,
                perPage: queryParams?.perPage || DEFAULT_PAGING_STATE.perPage,
                sortDirection: queryParams?.sortDirection || DEFAULT_PAGING_STATE.sortDirection,
                sort: queryParams?.sort || DEFAULT_PAGING_STATE.sort,
            });
        }
        // Reload only when the company or the tab changes; `queryParams` is read, not watched (filters reload on their own).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, tabValue]);

    const paging = { ...DEFAULT_PAGING_STATE, ...queryParams };
    const loading = status === 'loading';
    const resolved = status === 'resolved';
    const company = companyNameFor(id, `Company ${id ?? ''}`.trim());
    const failed = status === 'rejected';

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="API Integration"
                title={`${company} orders`}
                actions={tabValue === 0 ? (
                    <>
                        <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={() => setReportPopUp(true)}>
                            Export CSV
                        </Button>
                        {canManageOrders && (
                            <Button variant="contained" startIcon={<PrecisionManufacturingOutlinedIcon />} onClick={() => setProductionPopUp(true)}>
                                Move to production
                            </Button>
                        )}
                    </>
                ) : undefined}
            >
                <Tabs value={tabValue} onChange={(_event, value) => setTabValue(value)} aria-label="Orders and print files">
                    <Tab label="Orders" id="orders-tab-0" aria-controls="orders-panel-0" />
                    <Tab label="Images" id="orders-tab-1" aria-controls="orders-panel-1" />
                </Tabs>
            </PageHeader>

            {tabValue === 0 && (
                <div role="tabpanel" id="orders-panel-0" aria-labelledby="orders-tab-0">
                    <OrdersReport isPopUp={isReportPopUp} popUpHandler={() => setReportPopUp(false)} />
                    <OrdersProduction
                        queryParams={paging}
                        isPopUp={isProductionPopUp}
                        popUpHandler={() => setProductionPopUp(false)}
                        loadOrders={loadOrders}
                    />
                    <Panel padding="none">
                        <OrdersFilters key={filtersKey} queryParams={paging} loadOrders={loadOrders} loading={loading} onClear={clearFilters} />
                        {loading && (
                            <div style={{ padding: 16, display: 'grid', gap: 10 }} role="status" aria-label="Loading orders">
                                {[0, 1, 2, 3, 4, 5].map((row) => <Skeleton key={row} animation="wave" height={44} />)}
                            </div>
                        )}
                        {!loading && orders.length > 0 && <OrdersTable orders={orders} paging={paging} pagingUpdate={loadOrders} />}
                        {!loading && failed && orders.length === 0 && (
                            <EmptyState
                                title="Orders could not be loaded"
                                hint="The order service did not answer. Try again in a moment."
                                action={<Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => loadOrders(paging)}>Retry</Button>}
                            />
                        )}
                        {resolved && orders.length === 0 && (
                            <EmptyState
                                title="No orders match these filters"
                                hint="Try a wider date range or another status."
                                action={<Button variant="outlined" size="small" onClick={clearFilters}>Clear filters</Button>}
                            />
                        )}
                    </Panel>
                </div>
            )}

            {tabValue === 1 && (
                <div role="tabpanel" id="orders-panel-1" aria-labelledby="orders-tab-1">
                    <PrintFiles clientId={id || ''} />
                </div>
            )}
        </PageContainer>
    );
};

export default OrdersPage;
